import React, { useState } from "react";
import styles from "./PostContent.module.css";
import { useNavigate } from "react-router-dom";
import { formatPostDate } from "../utils/postUtils";
const CAPTION_PREVIEW_LENGTH = 220;

function MediaCarousel({ mediaValues, onError }) {
  const [current, setCurrent] = useState(0);

  const prev = (e) => {
    e.stopPropagation();
    setCurrent((i) => (i === 0 ? mediaValues.length - 1 : i - 1));
  };

  const next = (e) => {
    e.stopPropagation();
    setCurrent((i) => (i === mediaValues.length - 1 ? 0 : i + 1));
  };

  const handleTouchEnd = (e) => {
  const touchEnd = e.changedTouches[0].clientX;
  const touchStart = e.currentTarget.dataset.touchStart;

  if (!touchStart) return;

  const diff = Number(touchStart) - touchEnd;

  if (Math.abs(diff) < 50) return;

  if (diff > 0) {
    next(e); // Swipe left → next
  } else {
    prev(e); // Swipe right → previous
  }

  delete e.currentTarget.dataset.touchStart;
};

  const url =
    typeof mediaValues[current] === "string"
      ? mediaValues[current]
      : mediaValues[current]?.url;
  const isVideo = url?.endsWith?.(".mp4") || url?.includes?.("mp4");

  return (
    <div className={styles.carousel}>
      {/* Media */}
     <div
  className={styles.carouselTrack}
  onTouchStart={(e) => {
    e.currentTarget.dataset.touchStart = e.touches[0].clientX;
  }}
  onTouchEnd={handleTouchEnd}
>
        {isVideo ? (
          <video
            src={url}
            className={styles.carouselMedia}
            controls
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <img
            src={url}
            alt={`Media ${current + 1}`}
            className={styles.carouselMedia}
            onError={onError}
            loading="lazy"
          />
        )}
      </div>

      {/* Prev / Next buttons */}
           {/* Prev / Next buttons */}
      {mediaValues.length > 1 && (
        <>
          <button
            type="button"
            className={`${styles.carouselArrow} ${styles.carouselArrowLeft}`}
            onClick={prev}
            aria-label="Previous media"
          >
            ‹
          </button>

          <button
            type="button"
            className={`${styles.carouselArrow} ${styles.carouselArrowRight}`}
            onClick={next}
            aria-label="Next media"
          >
            ›
          </button>

          {/* Dots */}
          <div className={styles.carouselDots}>
            {mediaValues.map((_, idx) => (
              <span
                key={idx}
                className={`${styles.dot} ${
                  idx === current ? styles.dotActive : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrent(idx);
                }}
              />
            ))}
          </div>

          {/* Counter */}
          <div className={styles.carouselCounter}>
            {current + 1} / {mediaValues.length}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Original post embed (nested card for SharedPost) ────────────────────────
function OriginalPostEmbed({ originalPost }) {
  const navigate = useNavigate();
  const [isEmbedExpanded, setIsEmbedExpanded] = useState(false);
  const handleAvatarError = (e) => {
    e.target.onerror = null;
    e.target.src = "https://i.pravatar.cc/50";
  };

  const handleImageError = (e) => {
    e.target.style.display = "none";
  };

  const truncateEmbedCaption = (text = "") => {
  if (isEmbedExpanded) return text;

  if (text.length <= CAPTION_PREVIEW_LENGTH) {
    return text;
  }

  return text.substring(0, CAPTION_PREVIEW_LENGTH).trim() + "...";
};

  const handleGoToOriginalPost = (e) => {
    e.stopPropagation();
    navigate(`/post/${originalPost.id}?type=${originalPost.type || "Post"}`);
  };

  const handleGoToOriginalAuthor = (e) => {
    e.stopPropagation();
    if (originalPost.user?.id) {
      navigate(`/profile/${originalPost.user.id}`);
    }
  };

  if (!originalPost) {
    return (
      <div className={styles.originalPostEmbed}>
        <p className={styles.deletedPostNote}>
          This post is no longer available.
        </p>
      </div>
    );
  }

  const mediaValues = originalPost.media?.$values ?? [];
  const initials = originalPost.user?.name
    ?.split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className={styles.originalPostEmbed}
      onClick={handleGoToOriginalPost}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleGoToOriginalPost(e)}
      aria-label="View original post"
    >
      {/* Original post author header */}
      <div className={styles.embedHeader}>
        {originalPost.user?.profilePhotoUrl ? (
          <img
            src={originalPost.user?.profilePhotoUrl}
            alt={originalPost.user?.name}
            className={styles.embedAvatar}
            onError={handleAvatarError}
            onClick={handleGoToOriginalAuthor}
          />
        ) : (
          <div className={styles.noPhoto} onClick={handleGoToOriginalAuthor}>
            {initials || "?"}
          </div>
        )}
        <div className={styles.embedUserInfo}>
          <span
            className={styles.embedUserName}
            onClick={handleGoToOriginalAuthor}
          >
            {originalPost.user?.name}
          </span>
          {originalPost.createdAt && (
            <span className={styles.embedDate}>
              {formatPostDate(originalPost.createdAt)}
            </span>
          )}
        </div>
      </div>

      {/* Original post caption */}
      {originalPost.caption && (
  <p className={styles.embedCaption}>
    {truncateEmbedCaption(originalPost.caption)}

    {originalPost.caption.length > CAPTION_PREVIEW_LENGTH && (
      <span
        className={styles.moreText}
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsEmbedExpanded((prev) => !prev);
        }}
      >
        {isEmbedExpanded ? " See less" : " See more"}
      </span>
    )}
  </p>
)}

      {/* Original post media */}
      {mediaValues.length > 0 && (
        <MediaCarousel mediaValues={mediaValues} onError={handleImageError} />
      )}

      {/* Stats row */}
      <div className={styles.embedStats}>
        {originalPost.likesCount > 0 && (
          <span>
            {originalPost.likesCount} like
            {originalPost.likesCount !== 1 ? "s" : ""}
          </span>
        )}
        {originalPost.commentsCount > 0 && (
          <span>
            {originalPost.commentsCount} comment
            {originalPost.commentsCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Main PostContent ─────────────────────────────────────────────────────────
export default function PostContent({
  post,
  isEditing,
  editedContent,
  editedVisibility,
  setEditedVisibility,
  onContentChange,
  onSave,
  onCancel,
  isUpdating,
  disableNavigation = false,
}) {
  const navigate = useNavigate();
  const isSharedPost = post?.type === "SharedPost";

  const handleImageError = (e) => {
    e.target.style.display = "none";
  };

  const [isExpanded, setIsExpanded] = useState(false);

  const truncateCaption = (text = "") => {
    if (isExpanded) return text;

    if (text.length <= CAPTION_PREVIEW_LENGTH) {
      return text;
    }

    return text.substring(0, CAPTION_PREVIEW_LENGTH).trim() + "...";
  };

  // ── Edit mode (only applicable for regular posts) ──
  if (isEditing) {
    return (
      <div className={styles.editSection}>
        <textarea
          className={styles.editTextarea}
          value={editedContent}
          onChange={(e) => onContentChange(e.target.value)}
          rows={4}
          disabled={isUpdating}
          maxLength={5000}
          autoFocus
          placeholder="What's on your mind?"
        />

        <div className={styles.visibilitySelector}>
          <label>Visibility:</label>
          <select
            value={editedVisibility}
            onChange={(e) => setEditedVisibility(Number(e.target.value))}
            disabled={isUpdating}
            className={styles.visibilityDropdown}
          >
            <option value={0}>Public</option>
            <option value={1}>Connections</option>
            <option value={2}>Only Me</option>
          </select>
        </div>

        <div className={styles.editActions}>
          <button
            className={styles.saveBtn}
            onClick={onSave}
            disabled={isUpdating || !editedContent.trim()}
          >
            {isUpdating ? "Saving..." : "Save"}
          </button>
          <button
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={isUpdating}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const handleContentClick = () => {
    if (disableNavigation) return;

    if (post?.caption || post?.media?.$values?.length) {
      navigate(`/post/${post.id}?type=${post.type || "Post"}`);
    }
  };

  // ── SharedPost view ──
  if (isSharedPost) {
    return (
      <div className={styles.content} onClick={handleContentClick}>
        {/* Sharer's optional caption/comment */}
        {post.caption ? (
          <p className={styles.sharedCaption}>
            {truncateCaption(post.caption)}

            {post.caption && post.caption.length > CAPTION_PREVIEW_LENGTH && (
              <span
                className={styles.moreText}
                role="button"
                onClick={(e) => {
                  e.stopPropagation(); // prevent navigation
                  setIsExpanded((prev) => !prev);
                }}
              >
                {isExpanded ? " See less" : " See more"}
              </span>
            )}
          </p>
        ) : null}

        {/* Nested original post embed */}
        <OriginalPostEmbed originalPost={post.originalPost} />
      </div>
    );
  }

  // ── Regular Post view ──
  const mediaValues = post.media?.$values ?? [];

  return (
    <div className={styles.content} onClick={handleContentClick}>
      <p className={styles.text}>
        {truncateCaption(post?.caption)}

        {post?.caption?.length > CAPTION_PREVIEW_LENGTH && (
          <span
            className={styles.moreText}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? " See less" : " See more"}
          </span>
        )}
      </p>

      {/* Single media item */}
      {mediaValues.length > 0 && (
        <MediaCarousel mediaValues={mediaValues} onError={handleImageError} />
      )}
    </div>
  );
}
